import { z } from 'zod';
import { ModuleHealth } from './agent-api.js';
import { IpAddress, IsoTime, Labels, ServerId } from './basics.js';
import { Capabilities } from './capabilities.js';
import { ConfigStatusSummary } from './config.js';
import { Role } from './settings.js';
import { WazuhAgentHealth, WazuhAgentProblem } from './wazuh-agent.js';
/**
 * Servers as the dashboard sees them (knowledge doc sections 15 and 16).
 *
 * Two shapes, not one. A fleet list of two thousand servers must not carry every
 * server's capabilities and module health, and a detail page needs both — so the
 * list route serializes `ServerSummary` and the detail route `ServerDetail`. The
 * split is enforced by the response schema rather than by remembering which fields
 * to leave out.
 *
 * `publicKey` appears in neither. It is not a secret, but it is the credential the
 * whole agent contract rests on, and nothing in React has a use for it beyond the
 * fingerprint — which the pairing screen shows, from `PairingRequestSummary`'s own
 * `fingerprint` field rather than by carrying the key across and hashing it there.
 */
/** Section 16. `pending` is awaiting approval; `revoked` has had its key dropped. */
export const ServerStatus = z.enum(['pending', 'active', 'offline', 'revoked']);
export const ServerGroupId = z.uuid();
/** Enough of a group to render it beside a server without a second request. */
export const ServerGroupRef = z.object({
    id: ServerGroupId,
    name: z.string().min(1).max(100),
});
export const ServerGroup = ServerGroupRef.extend({
    roles: z.array(Role),
    /** So an admin can see which groups are worth keeping. */
    serverCount: z.number().int().nonnegative(),
    createdAt: IsoTime,
});
export const CreateServerGroupRequest = z.object({
    name: z.string().trim().min(1).max(100),
    /** Applied to every server that joins through a token naming this group. */
    roles: z.array(Role).max(8).default([]),
});
/**
 * `PATCH /v1/server-groups/:id` (P11.5): a new name, new roles, or both. Roles are
 * an input to every member's settings merge (section 7), so changing them builds
 * each member a new version.
 */
export const UpdateServerGroupRequest = z
    .object({
    name: z.string().trim().min(1).max(100).optional(),
    roles: z.array(Role).max(8).optional(),
})
    .strict()
    .refine((body) => body.name !== undefined || body.roles !== undefined, {
    message: 'name or roles',
});
/**
 * What on a server needs a person (P11.7), for the fleet list: modules whose
 * health is not ok, whether its newest settings failed, and the high and critical
 * events of the last week nobody acknowledged.
 */
export const ServerAttention = z.object({
    unhealthy: z.array(z.string().max(64)).max(20),
    settingsFailed: z.boolean(),
    openHigh: z.number().int().nonnegative(),
    /** Its Wazuh agent, as the central manager sees it (P11.8.5); null when fine or not central. */
    wazuhAgent: WazuhAgentProblem.nullable().default(null),
});
/** One row in the fleet list. */
export const ServerSummary = z.object({
    id: ServerId,
    name: z.string().min(1).max(255),
    hostname: z.string().min(1).max(255),
    /**
     * Self-reported at enrollment and never used to decide anything (section 10).
     * It is shown because it is usually the private address an operator recognises.
     */
    ip: IpAddress.nullable(),
    os: z.string().max(100).nullable(),
    status: ServerStatus,
    roles: z.array(Role),
    group: ServerGroupRef.nullable(),
    labels: Labels,
    agentVersion: z.string().max(50).nullable(),
    lastHeartbeat: IsoTime.nullable(),
    createdAt: IsoTime,
    /** The fleet list's; absent where a server is read another way. */
    attention: ServerAttention.optional(),
});
export const ServerDetail = ServerSummary.extend({
    appliedConfigVersion: z.number().int().nonnegative(),
    /** Per-module `{ ok, detail }` from the last heartbeat; null before the first. */
    moduleHealth: z.record(z.string().max(64), ModuleHealth).nullable(),
    /** Section 20 makes this load-bearing: React hides actions a server cannot do. */
    capabilities: Capabilities.nullable(),
    revokedAt: IsoTime.nullable(),
    /** Which token brought it in, so section 10's "servers that used it" can be read
     * from either end. Null for a server whose token has since been deleted. */
    enrolledWithTokenId: z.uuid().nullable(),
    /**
     * Section 15 lists "config status" among what this route returns, and section 11
     * wants it per server. Null until the settings tables have anything to say about
     * this machine — which is every server until it first syncs.
     */
    configStatus: ConfigStatusSummary.nullable(),
    /**
     * Its Wazuh agent as the central manager's API last reported it (P11.8.5). Null
     * where the API reads no manager or the server has never asked to enrol.
     */
    wazuhAgent: WazuhAgentHealth.nullable().default(null),
});
/** Section 15: filter by server, group, status; paginated with a cursor. */
export const SERVER_PAGE_DEFAULT = 50;
export const SERVER_PAGE_MAX = 200;
export const ServerListQuery = z.object({
    status: ServerStatus.optional(),
    group_id: ServerGroupId.optional(),
    /** Matches the start of the name or hostname. Not a regular expression. */
    q: z.string().trim().min(1).max(100).optional(),
    limit: z.coerce.number().int().min(1).max(SERVER_PAGE_MAX).default(SERVER_PAGE_DEFAULT),
    cursor: z.string().max(512).optional(),
});
/**
 * Section 7: "React shows detected roles and lets an admin change them." A server's
 * group and its display name are editable for the same reason.
 *
 * Every field is optional and at least one must be present, so a PATCH that would
 * change nothing is a mistake the schema catches rather than a no-op write and an
 * audit entry saying nothing happened.
 */
export const UpdateServerRequest = z
    .object({
    name: z.string().trim().min(1).max(255).optional(),
    roles: z.array(Role).max(8).optional(),
    /** `null` removes the server from its group. */
    groupId: ServerGroupId.nullable().optional(),
})
    .refine((value) => Object.values(value).some((field) => field !== undefined), 'change at least one field');
/**
 * Approving a server that enrolled through a `needs_approval` token (section 10).
 *
 * Until then it may send a heartbeat and nothing else, which is deliberate: an
 * admin approving a server needs to be able to tell a running agent from one that
 * never started, and the heartbeat is what says so.
 */
export const ApproveServerResponse = z.object({ server: ServerDetail });
/**
 * Removing a server drops its public key, which is exactly what section 10 means
 * by revocation: the next token it signs fails to verify. The row stays, so its
 * events and metrics keep a server to belong to.
 */
export const RemoveServerResponse = z.object({
    id: ServerId,
    status: z.literal('revoked'),
    revokedAt: IsoTime,
});
//# sourceMappingURL=server.js.map